import { I18n } from "@assets/strings";
import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Environment } from "@core/config/environment";
import { Category } from "@domain/lms/category/category";
import { Card } from "@ui/components/containers/card";
import { row } from "./layout";

const INITIAL_CATEGORIES_COUNT = 4;

interface CategoriesViewProps extends React.HTMLAttributes<HTMLDivElement> {
	categories: Category[];
	showAll?: boolean;
	onItemClick?: (cat: Category) => void;
}
export const CategoriesView: React.FC<CategoriesViewProps> = ({ categories, showAll, onItemClick, ...htmlProps }) => {
	const [showAllCategories, setShowAllCategories] = useState(showAll || categories.length < INITIAL_CATEGORIES_COUNT);
	const displayedCategories = useMemo(() => {
		const sortedCategories = categories.sort((a, b) => I18n.t(`category.${a}`).length - I18n.t(`category.${b}`).length);
		return showAllCategories ? sortedCategories : sortedCategories.slice(0, INITIAL_CATEGORIES_COUNT);
	}, [showAllCategories, categories]);

	return displayedCategories ? (
		<Container {...htmlProps}>
			{displayedCategories.map((category) => (
				<CategoryView key={category} onMouseDown={() => onItemClick?.(category)} clickable={!!onItemClick}>
					{I18n.t(`category.${category}`)}
				</CategoryView>
			))}
			{!showAll && categories.length > INITIAL_CATEGORIES_COUNT && (
				<CategoryView onClick={() => setShowAllCategories(!showAllCategories)} isExpander>
					{showAllCategories ? I18n.t("profile.card.categories.see_less") : I18n.t("profile.card.categories.see_more")}
				</CategoryView>
			)}
		</Container>
	) : (
		<></>
	);
};

const Container = styled.div`
	${row(6, "center", "center")};
	flex-wrap: wrap;
`;

const CategoryView = styled(Card).withConfig({
	shouldForwardProp: (prop) => !["clickable", "isExpander"].includes(prop),
})<{ clickable?: boolean; isExpander?: boolean }>`
	${({ theme }) => theme.textStyles.light};
	cursor: ${({ clickable, isExpander }) => (clickable || isExpander ? "pointer" : "default")};
	border: none;
	box-shadow: ${Environment.APP_VARIANT === "jaangal" ? "none" : "0 4px 16px 0 red"};
	background-color: ${({ theme }) =>
		Environment.APP_VARIANT === "jaangal" ? theme.colors.simplegray : theme.colors.white};
	border-top-left-radius: ${Environment.APP_VARIANT === "jaangal" ? "none" : 0};
	margin: 3px;
	padding: ${Environment.APP_VARIANT === "jaangal" ? "8px 12px" : 8};
	will-change: background-color;
	transition: background-color 0.2s linear;

	${({ clickable, isExpander }) =>
		isExpander
			? css`
					font-weight: bold;
					color: ${({ theme }) => theme.colors.secondary};
				`
			: clickable
				? css`
						:hover {
							background-color: ${({ theme }) => theme.colors.lightergray};
						}
					`
				: undefined}
`;
